import { AssignedEquipmentModel } from './../../../../models/equipmentsModel';
import { getUsersIncludingTerminated, getUsersList } from '../../../../core/store/users/users.actions';
import { SelectItem } from 'primeng/api';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { deepClone } from 'src/app/helpers/utils';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import {
  loadUserEquipments,
  saveReturnEquipment,
  changedReturnEquipment,
  loadManageAssetLocations
} from 'src/app/core/store/equipment/equipment.actions';
import { formatDate } from '@angular/common';
import { ManageAssetService } from 'src/app/core/services/manage-asset.service';
import { ManageAssetBarcodeLookupOptionModel, ManageAssetBarcodeLookupResultModel, ManageAssetCampusModel } from 'src/app/models/manageAssetModels';
@Component({
    selector: 'app-return-equipment',
    templateUrl: './return-equipment.component.html',
    styleUrls: ['./return-equipment.component.scss'],
    standalone: false
})
export class ReturnEquipmentComponent extends BaseComponent implements OnInit {
  readonly returnStatusOptions: SelectItem[] = [
    { label: 'Working', value: 1 },
    { label: 'Damaged', value: 2 },
    { label: 'Destroyed', value: 3 },
    { label: 'Not Returned', value: 4 }
  ];
  readonly assetItemConditionOptions = [
    { id: 1, assetItemCondition: 'New' },
    { id: 2, assetItemCondition: 'Excellent / Like New' },
    { id: 3, assetItemCondition: 'Good' },
    { id: 4, assetItemCondition: 'Fair / Average' },
    { id: 5, assetItemCondition: 'Poor' },
    { id: 6, assetItemCondition: 'Unserviceable / Damaged' },
    { id: 7, assetItemCondition: 'Obsolete / End of Life' }
  ];
  @Input() initialUserID: number = null;
  @Input() initialProgramID: number = null;
  @Input() initialAssetAssignedID: number = null;
  @Input() initialSelection: AssignedEquipmentModel = null;
  @Input() selectionKey = 0;

  canEdit: boolean;
  assignedEquipmentsToUser: AssignedEquipmentModel[];
  returningEquipments: AssignedEquipmentModel[];
  usersList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  returnCampusList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  locationList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  returnTargetType: 'user' | 'campus' = 'user';
  userID: number;
  assignedProgramID: number = null;
  showErrors = false;
  savingReturnEquipments = false;
  loadingUserEquipments = false;
  includeTerminated = false;
  barcode = '';
  barcodeLookupLoading = false;
  barcodeLookupResult: ManageAssetBarcodeLookupResultModel;
  barcodeLookupError: string;
  barcodeOnlyReturns: AssignedEquipmentModel[] = [];
  pendingScannedUserReturns: AssignedEquipmentModel[] = [];
  pendingSelectedAssetAssignedID: number = null;
  showReturnConfirmDialog = false;
  reassignTargetType: 'user' | 'campus' = 'user';
  reassignToUserID: number = null;
  reassignToProgramID: number = null;
  reassignReturnDateString: string = null;
  reassignNote: string = '';
  reassignCampusList: SelectItem[] = [{ label: 'loading...', value: undefined }];
  reassignErrorMessage: string = null;
  private activeUsers = [];
  private usersIncludingTerminated = [];
  private barcodeLookupDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  /** Reference counts: two p-selects per row can overlap show/hide when switching focus. */
  private readonly openAssignedReturnSelectCounts = new Map<string, number>();
  private readonly openBarcodeReturnSelectCounts = new Map<string, number>();

  constructor(
    private store: Store<State>,
    private manageAssetService: ManageAssetService,
  ) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(getUsersList());
    this.subs.push(
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canEdit = permissionIDs && !!permissionIDs.find(p => p === 3058); //equipment manager
      }),
      this.store.select(s => s.equipmentState.assignedEquipmentsToUser).subscribe(assignedEquipmentsToUser => {
        this.assignedEquipmentsToUser = deepClone(assignedEquipmentsToUser);
        this.applyPendingScannedUserReturns();
        this.refreshReturningEquipments();
      }),
      this.store.select(s => s.equipmentState.savingReturnEquipments).subscribe(savingReturnEquipments => {
        this.savingReturnEquipments = savingReturnEquipments;
      }),
      this.store.select(s => s.equipmentState.loadingUserEquipments).subscribe(loadingUserEquipments => {
        this.loadingUserEquipments = !!loadingUserEquipments;
      }),
      this.store.select(s => s.usersState.usersList).subscribe(usersList => {
        this.activeUsers = usersList ?? [];
        this.updateUsersList();
        if (this.userID) {
          this.syncIncludeTerminatedForUser(this.userID);
        }
      }),
      this.store.select(s => s.usersState.usersListIncludingTerminated).subscribe(usersListIncludingTerminated => {
        this.usersIncludingTerminated = usersListIncludingTerminated ?? [];
        this.updateUsersList();
        if (this.userID) {
          this.syncIncludeTerminatedForUser(this.userID);
        }
      }),
      this.store.select(s => s.equipmentState.manageAssetLocations).subscribe(locations => {
        const mappedLocations = locations && locations.length > 0
          ? locations.map(location => ({ label: `${location.name} (${location.programName})`, value: location.id }))
          : [{ label: 'loading...', value: undefined }];
        this.locationList = mappedLocations;
      })
    );

    this.store.dispatch(getUsersIncludingTerminated());
    this.store.dispatch(loadManageAssetLocations());
    this.manageAssetService.getAssignableCampuses().subscribe({
      next: (campuses: ManageAssetCampusModel[]) => {
        const mappedCampuses = (campuses ?? []).map(c => ({ label: c.programName, value: c.id }));
        this.reassignCampusList = [...mappedCampuses];
        this.reassignCampusList.unshift({ label: '', value: null });
        this.applyReturnCampusList(mappedCampuses);
      },
      error: () => {
        this.reassignCampusList = [{ label: '', value: null }];
        this.applyReturnCampusList([]);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectionKey && !changes.selectionKey.firstChange) {
      this.applyInitialSelection();
      return;
    }

    if (changes.selectionKey?.firstChange && this.selectionKey) {
      this.applyInitialSelection();
    }
  }

  override ngOnDestroy() {
    if (this.barcodeLookupDebounceHandle) {
      clearTimeout(this.barcodeLookupDebounceHandle);
      this.barcodeLookupDebounceHandle = null;
    }

    super.ngOnDestroy();
  }

  requestSave() {
    if (this.disableReturnControls) {
      return;
    }

    const allReturns = [...this.returningEquipments, ...this.barcodeOnlyReturns];
    const hasInvalidReturns = allReturns.some(item =>
      !item.assetLocationID
      || !item.returnNotes || !item.returnNotes.trim()
      || this.isConditionSelectionInvalid(item));
    const reassignValidationError = this.getReassignValidationError(allReturns);
    this.reassignErrorMessage = reassignValidationError;

    if (allReturns.length > 0 && !hasInvalidReturns && !reassignValidationError) {
      this.showErrors = false;
      this.showReturnConfirmDialog = true;
    } else {
      this.showErrors = true;
    }
  }

  save() {
    if (this.disableReturnControls) {
      return;
    }

    const allReturns = [...this.returningEquipments, ...this.barcodeOnlyReturns];
    const hasInvalidReturns = allReturns.some(item =>
      !item.assetLocationID
      || !item.returnNotes || !item.returnNotes.trim()
      || this.isConditionSelectionInvalid(item));
    const reassignValidationError = this.getReassignValidationError(allReturns);
    this.reassignErrorMessage = reassignValidationError;

    if (allReturns.length < 1 || hasInvalidReturns || !!reassignValidationError) {
      this.showErrors = true;
      this.showReturnConfirmDialog = false;
      return;
    }

    const requestItems = allReturns.map(item => ({
      ...item,
      reassignToUserID: this.hasReassignTarget ? this.reassignToUserID ?? undefined : undefined,
      reassignToProgramID: this.hasReassignTarget ? this.reassignToProgramID ?? undefined : undefined,
      reassignReturnDate: this.hasReassignTarget && this.reassignReturnDateString ? new Date(this.reassignReturnDateString) : undefined,
      reassignNote: this.hasReassignTarget && this.reassignNote ? this.reassignNote : undefined
    }));
    this.store.dispatch(saveReturnEquipment({ returningEquipments: requestItems, isReassign: this.hasReassignTarget }));
    this.showReturnConfirmDialog = false;
  }

  cancelSave() {
    if (this.disableReturnControls) {
      return;
    }

    this.showReturnConfirmDialog = false;
  }

  includeTerminatedChange(event){
    this.includeTerminated = !!event?.checked;
    this.updateUsersList();
  }

  changedUser() {
    this.returnTargetType = 'user';
    this.assignedProgramID = null;
    if (!this.userID) {
      this.assignedEquipmentsToUser = [];
      this.refreshReturningEquipments();
      return;
    }

    this.assignedEquipmentsToUser = [];
    this.refreshReturningEquipments();
    this.store.dispatch(loadUserEquipments({ userID: this.userID }));
  }

  changedReturnTargetType() {
    if (this.returnTargetType === 'campus') {
      this.userID = null;
      this.includeTerminated = false;
    } else {
      this.assignedProgramID = null;
    }
    this.assignedEquipmentsToUser = [];
    this.refreshReturningEquipments();
  }

  changedCampus() {
    this.returnTargetType = 'campus';
    this.userID = null;
    if (!this.assignedProgramID) {
      this.assignedEquipmentsToUser = [];
      this.refreshReturningEquipments();
      return;
    }

    this.assignedEquipmentsToUser = [];
    this.refreshReturningEquipments();
    this.store.dispatch(loadUserEquipments({ assignedProgramID: this.assignedProgramID }));
  }

  changedEquipment(assignedEquipment: AssignedEquipmentModel) {
    this.reassignErrorMessage = null;
    if (assignedEquipment?.returned) {
      this.autoSelectGroupedReturns(assignedEquipment);
      this.initializeReturnDefaults(assignedEquipment);
    }
    this.updateAllowedAssetItemConditions(assignedEquipment);
    this.refreshReturningEquipments();
    this.store.dispatch(changedReturnEquipment({ assignedEquipment }));
  }

  changedBarcodeOnlyReturn() {
    this.reassignErrorMessage = null;
    (this.barcodeOnlyReturns ?? []).forEach(item => this.updateAllowedAssetItemConditions(item));
    this.refreshReturningEquipments();
  }

  changedReassignTargetType() {
    this.reassignErrorMessage = null;
    if (this.reassignTargetType === 'user') {
      this.reassignToProgramID = null;
    } else {
      this.reassignToUserID = null;
    }
  }

  changedReassignTarget() {
    this.reassignErrorMessage = null;
  }

  getAllReturns() {
    return [...(this.returningEquipments ?? []), ...(this.barcodeOnlyReturns ?? [])];
  }

  getReturnAssigneeLabel(item: AssignedEquipmentModel) {
    return item.assignedToUserName || item.programName || 'General Inventory';
  }

  getReturnDescription(item: AssignedEquipmentModel) {
    return item.description || item.equipment?.description || '-';
  }

  getLocationLabel(assetLocationID: number) {
    if (!assetLocationID) {
      return '-';
    }

    return this.locationList.find(x => x.value === assetLocationID)?.label ?? '-';
  }

  getReturnStatusLabelForItem(item: AssignedEquipmentModel) {
    return this.getReturnStatusLabel(item.assetReturnStatusID || 1);
  }

  getConditionLabelForItem(item: AssignedEquipmentModel) {
    if (!item?.assetItemConditionID) {
      return '-';
    }
    return this.assetItemConditionOptions.find(x => x.id === item.assetItemConditionID)?.assetItemCondition ?? '-';
  }

  getReassignAssigneeLabel(): string {
    if (!this.hasReassignTarget) {
      return '-';
    }

    if (this.reassignTargetType === 'campus') {
      const campus = (this.reassignCampusList ?? []).find(x => x.value === this.reassignToProgramID);
      return campus?.label || '-';
    }

    const user = (this.usersList ?? []).find(x => x.value === this.reassignToUserID);
    return user?.label || '-';
  }

  onBarcodeEnterKey(event: KeyboardEvent) {
    if (this.disableReturnControls || this.barcodeLookupLoading) {
      return;
    }
    event.preventDefault();
    this.lookupBarcode();
  }

  onBarcodeInputChange() {
    if (this.disableReturnControls) {
      return;
    }

    if (this.barcodeLookupDebounceHandle) {
      clearTimeout(this.barcodeLookupDebounceHandle);
      this.barcodeLookupDebounceHandle = null;
    }

    const barcode = this.barcode?.trim();
    if (!barcode || barcode.length < 2) {
      this.barcodeLookupResult = null;
      this.barcodeLookupError = null;
      return;
    }

    this.barcodeLookupDebounceHandle = setTimeout(() => {
      this.lookupBarcode(true);
    }, 300);
  }

  lookupBarcode(suppressErrors = false) {
    const barcode = this.barcode?.trim();
    this.barcodeLookupResult = null;
    this.barcodeLookupError = null;

    if (!barcode) {
      if (!suppressErrors) {
        this.barcodeLookupError = 'Barcode is required.';
      }
      return;
    }

    this.barcodeLookupLoading = true;
    this.manageAssetService.lookupBarcode({ barcode }).subscribe({
      next: (result) => {
        this.barcodeLookupResult = result;
        if (!result?.found && !suppressErrors) {
          this.barcodeLookupError = result?.errorMessage || 'No active asset assignment was found for this barcode.';
        }
      },
      error: (err) => {
        if (!suppressErrors) {
          this.barcodeLookupError = err?.error || 'Barcode lookup failed.';
        }
      },
      complete: () => {
        this.barcodeLookupLoading = false;
      }
    });
  }

  queueBarcodeReturn(option: ManageAssetBarcodeLookupOptionModel) {
    const pendingReturn = this.buildBarcodeReturn(option);

    if (option.assignedToUserID) {
      this.upsertPendingScannedUserReturn(pendingReturn);
      this.userID = option.assignedToUserID;
      this.store.dispatch(loadUserEquipments({ userID: option.assignedToUserID }));
    } else {
      this.upsertBarcodeOnlyReturn(pendingReturn);
    }

    this.barcodeLookupResult = null;
    this.barcodeLookupError = null;
    this.barcode = '';
    this.showErrors = false;
  }

  removeBarcodeOnlyReturn(item: AssignedEquipmentModel) {
    this.barcodeOnlyReturns = this.barcodeOnlyReturns.filter(x => x.assetAssignedID !== item.assetAssignedID);
    this.refreshReturningEquipments();
  }

  trackByFn(index, obj){
      return index;
  }

  private applyInitialSelection() {
    this.pendingSelectedAssetAssignedID = this.initialAssetAssignedID ?? null;
    this.showErrors = false;
    this.barcodeLookupResult = null;
    this.barcodeLookupError = null;
    this.barcodeOnlyReturns = [];
    this.pendingScannedUserReturns = [];

    this.userID = this.initialUserID ?? null;
    this.assignedProgramID = this.initialProgramID ?? null;
    this.returnTargetType = this.assignedProgramID ? 'campus' : 'user';

    if (!this.userID && !this.assignedProgramID) {
      this.seedInitialSelectionWithoutUser();
      this.assignedEquipmentsToUser = [];
      this.refreshReturningEquipments();
      return;
    }

    if (this.userID) {
      this.syncIncludeTerminatedForUser(this.initialUserID);
    }

    this.assignedEquipmentsToUser = [];
    this.refreshReturningEquipments();
    this.store.dispatch(loadUserEquipments({
      userID: this.userID || undefined,
      assignedProgramID: this.assignedProgramID || undefined
    }));
  }

  get isDirectSelectionMode() {
    return !!this.initialAssetAssignedID || !!this.initialSelection?.assetAssignedID;
  }

  get disableReturnControls() {
    return this.loadingUserEquipments || this.savingReturnEquipments;
  }

  get hasReturnTargetSelection(): boolean {
    return this.returnTargetType === 'campus'
      ? !!this.assignedProgramID
      : !!this.userID;
  }

  get showNoAssetsToReturnMessage(): boolean {
    return this.hasReturnTargetSelection
      && !this.loadingUserEquipments
      && (this.assignedEquipmentsToUser?.length ?? 0) < 1;
  }

  get hasReassignTarget(): boolean {
    return (this.reassignToUserID ?? 0) > 0 || (this.reassignToProgramID ?? 0) > 0;
  }

  get submitActionLabel(): string {
    return this.hasReassignTarget ? 'Re-assign' : 'Return';
  }

  get partialReturnKitSummaries() {
    const selectedReturns = this.getAllReturns().filter(item => item.returned);
    const selectedGroups = new Map<string, { selectedCount: number; expectedCount: number; }>();

    selectedReturns.forEach(item => {
      const groupNames = this.getGroupNames(item);
      if (!groupNames.length) {
        return;
      }

      const expectedCount = item?.equipment?.groupMemberCount ?? 0;
      groupNames.forEach(groupName => {
        const current = selectedGroups.get(groupName) ?? { selectedCount: 0, expectedCount: 0 };
        current.selectedCount += 1;
        current.expectedCount = Math.max(current.expectedCount, expectedCount);
        selectedGroups.set(groupName, current);
      });
    });

    return Array.from(selectedGroups.entries())
      .map(([groupName, counts]) => ({
        groupName,
        selectedCount: counts.selectedCount,
        expectedCount: counts.expectedCount > 0 ? counts.expectedCount : counts.selectedCount,
      }))
      .filter(x => x.selectedCount < x.expectedCount)
      .sort((a, b) => a.groupName.localeCompare(b.groupName));
  }

  get hasPartialReturnKits(): boolean {
    return this.partialReturnKitSummaries.length > 0;
  }

  private refreshReturningEquipments() {
    this.returningEquipments = (this.assignedEquipmentsToUser ?? []).filter(e => e.returned);

    [...this.returningEquipments, ...this.barcodeOnlyReturns].forEach(item => {
      this.initializeReturnDefaults(item);
      this.updateAllowedAssetItemConditions(item);
    });
  }

  private applyPendingScannedUserReturns() {
    if (!this.assignedEquipmentsToUser?.length || !this.pendingScannedUserReturns.length) {
      this.applyPendingSelectedReturn();
      return;
    }

    const unresolved: AssignedEquipmentModel[] = [];

    this.pendingScannedUserReturns.forEach((pendingReturn) => {
      const match = this.assignedEquipmentsToUser.find(item => item.assetAssignedID === pendingReturn.assetAssignedID);
      if (!match) {
        unresolved.push(pendingReturn);
        return;
      }

      match.returned = true;
      this.initializeReturnDefaults(match);
      match.assetLocationID = match.assetLocationID || pendingReturn.assetLocationID;
      match.returnNotes = match.returnNotes || pendingReturn.returnNotes;
      this.updateAllowedAssetItemConditions(match);
    });

    this.pendingScannedUserReturns = unresolved;
    this.applyPendingSelectedReturn();
  }

  private applyPendingSelectedReturn() {
    if (!this.assignedEquipmentsToUser?.length || !this.pendingSelectedAssetAssignedID) {
      return;
    }

    const match = this.assignedEquipmentsToUser.find(item => item.assetAssignedID === this.pendingSelectedAssetAssignedID);
    if (!match) {
      return;
    }

    match.returned = true;
    this.initializeReturnDefaults(match);
    this.updateAllowedAssetItemConditions(match);
    this.pendingSelectedAssetAssignedID = null;
    this.refreshReturningEquipments();
  }

  private buildBarcodeReturn(option: ManageAssetBarcodeLookupOptionModel): AssignedEquipmentModel {
    return {
      id: option.assetAssignedID,
      assetAssignedID: option.assetAssignedID,
      equipmentID: option.assetAssignedID,
      assetInventoryID: option.assetInventoryID,
      assetItemID: option.assetItemID,
      isInventoryAssignment: option.isInventoryAssignment,
      userID: option.assignedToUserID,
      assignedToUserName: option.assignedToUserName,
      assetCategory: this.barcodeLookupResult?.assetCategory,
      description: this.barcodeLookupResult?.description,
      barcode: this.barcodeLookupResult?.barcode,
      inventoryAssetLocation: option.suggestedAssetLocation,
      assetLocationID: option.suggestedAssetLocationID,
      assetReturnStatusID: this.getDefaultReturnStatusForCondition(option.assetItemConditionID),
      requireItemCondition: option.requireItemCondition === true,
      assetItemConditionID: option.assetItemConditionID ?? null,
      originalAssetItemConditionID: option.assetItemConditionID ?? null,
      allowedAssetItemConditions: [],
      returnStatus: this.getReturnStatusLabel(this.getDefaultReturnStatusForCondition(option.assetItemConditionID)),
      returnNotes: '',
      returned: true,
      dateReturnedString: this.todayDateString(),
      actionLabel: option.actionLabel,
      equipment: {
        id: option.assetItemID ?? option.assetInventoryID,
        assetInventoryID: option.assetInventoryID,
        assetItemID: option.assetItemID,
        isInventoryAssignment: option.isInventoryAssignment,
        assetCategory: this.barcodeLookupResult?.assetCategory,
        description: this.barcodeLookupResult?.description,
        barcode: this.barcodeLookupResult?.barcode,
        inventoryAssetLocation: option.suggestedAssetLocation,
        serialNumber: this.barcodeLookupResult?.serialNumber ?? '',
        brand: '',
        model: '',
        serviceTag: '',
        expressServiceCode: '',
        type: this.barcodeLookupResult?.assetType ?? 'Asset',
        purchaseDate: null,
        assetTag: this.barcodeLookupResult?.barcode ?? '',
        label: [this.barcodeLookupResult?.assetType, this.barcodeLookupResult?.description, this.barcodeLookupResult?.barcode].filter(x => !!x).join(' - '),
        archived: false,
        department: '',
        pricing: '',
        purchaseVendor: '',
        purchaseLocation: '',
        storedLocation: option.suggestedAssetLocation ?? '',
        storedLocationID: option.suggestedAssetLocationID ?? 0,
        storedOther: '',
        paymentMethod: '',
        size: '',
        classCode: '',
        classification: '',
        classificationOther: '',
        mainProgramID: 0,
        mainProgramName: '',
        equipmentTypeID: 0
      }
    };
  }

  private seedInitialSelectionWithoutUser() {
    const selection = this.initialSelection ? deepClone(this.initialSelection) : null;
    if (!selection?.assetAssignedID) {
      return;
    }

    selection.returned = true;
    if (selection.originalAssetItemConditionID === undefined) {
      selection.originalAssetItemConditionID = selection.assetItemConditionID ?? null;
    }
    selection.assetReturnStatusID = selection.assetReturnStatusID || this.getDefaultReturnStatusForCondition(selection.assetItemConditionID);
    selection.allowedAssetItemConditions = selection.allowedAssetItemConditions ?? [];
    selection.returnStatus = selection.returnStatus || this.getReturnStatusLabel(selection.assetReturnStatusID);
    selection.returnNotes = selection.returnNotes ?? '';
    selection.dateReturnedString = selection.dateReturnedString || this.todayDateString();
    selection.actionLabel = selection.actionLabel
      || (selection.assignedToUserName
        ? `Return from ${selection.assignedToUserName}`
        : (selection.programName ? `Return from ${selection.programName}` : 'Return to General Inventory'));
    selection.equipment = selection.equipment || {
      id: selection.assetItemID ?? selection.assetInventoryID,
      assetInventoryID: selection.assetInventoryID,
      assetItemID: selection.assetItemID,
      isInventoryAssignment: selection.isInventoryAssignment,
      assetCategory: selection.assetCategory,
      description: selection.description,
      barcode: selection.barcode,
      inventoryAssetLocation: selection.inventoryAssetLocation,
      serialNumber: '',
      brand: '',
      model: '',
      serviceTag: '',
      expressServiceCode: '',
      type: 'Asset',
      purchaseDate: null,
      assetTag: selection.barcode ?? '',
      label: [selection.description, selection.barcode].filter(x => !!x).join(' - '),
      archived: false,
      department: '',
      pricing: '',
      purchaseVendor: '',
      purchaseLocation: '',
      storedLocation: selection.inventoryAssetLocation ?? '',
      storedLocationID: 0,
      storedOther: '',
      paymentMethod: '',
      size: '',
      classCode: '',
      classification: '',
      classificationOther: '',
      mainProgramID: 0,
      mainProgramName: '',
      equipmentTypeID: 0
    };

    this.upsertBarcodeOnlyReturn(selection);
  }

  private upsertPendingScannedUserReturn(item: AssignedEquipmentModel) {
    const existingIndex = this.pendingScannedUserReturns.findIndex(x => x.assetAssignedID === item.assetAssignedID);
    if (existingIndex >= 0) {
      this.pendingScannedUserReturns.splice(existingIndex, 1, item);
      return;
    }

    this.pendingScannedUserReturns.push(item);
  }

  private upsertBarcodeOnlyReturn(item: AssignedEquipmentModel) {
    const existingIndex = this.barcodeOnlyReturns.findIndex(x => x.assetAssignedID === item.assetAssignedID);
    if (existingIndex >= 0) {
      this.barcodeOnlyReturns.splice(existingIndex, 1, item);
    } else {
      this.barcodeOnlyReturns.push(item);
    }

    this.refreshReturningEquipments();
  }

  private todayDateString() {
    return formatDate(new Date(), 'yyyy-MM-dd', 'en-US');
  }

  private autoSelectGroupedReturns(seed: AssignedEquipmentModel) {
    const groupNames = this.getGroupNames(seed);
    if (!groupNames.length || !(this.assignedEquipmentsToUser?.length > 0)) {
      return;
    }

    this.assignedEquipmentsToUser.forEach(item => {
      if (item.assetAssignedID === seed.assetAssignedID) {
        return;
      }
      if (!this.sharesAnyGroup(seed, item)) {
        return;
      }
      if (item.returned) {
        return;
      }

      item.returned = true;
      this.initializeReturnDefaults(item);
      this.updateAllowedAssetItemConditions(item);
      this.store.dispatch(changedReturnEquipment({ assignedEquipment: item }));
    });
  }

  private initializeReturnDefaults(item: AssignedEquipmentModel) {
    if (!item) {
      return;
    }
    if (item.originalAssetItemConditionID === undefined) {
      item.originalAssetItemConditionID = item.assetItemConditionID ?? null;
    }
    if (!item.dateReturnedString) {
      item.dateReturnedString = this.todayDateString();
    }
    if (!item.assetReturnStatusID) {
      item.assetReturnStatusID = this.getDefaultReturnStatusForCondition(item.assetItemConditionID);
    }
    item.returnStatus = this.getReturnStatusLabel(item.assetReturnStatusID);
  }

  private getGroupNames(item: AssignedEquipmentModel): string[] {
    return (item?.equipment?.assetGroups ?? [])
      .map(x => `${x ?? ''}`.trim())
      .filter(x => x.length > 0);
  }

  private sharesAnyGroup(a: AssignedEquipmentModel, b: AssignedEquipmentModel): boolean {
    const aGroups = this.getGroupNames(a);
    const bGroups = this.getGroupNames(b);
    if (!aGroups.length || !bGroups.length) {
      return false;
    }

    const bGroupSet = new Set(bGroups);
    return aGroups.some(group => bGroupSet.has(group));
  }

  private updateUsersList() {
    const source = this.includeTerminated ? this.usersIncludingTerminated : this.activeUsers;
    this.usersList = source && source.length > 0
      ? source.map(u => ({ label: `${u.firstName} ${u.lastName}`, value: u.id }))
      : [{ label: 'loading...', value: undefined }];
    this.usersList.unshift({ label: '', value: null });
  }

  private applyReturnCampusList(campuses: SelectItem[]) {
    // Keep a separate list for return target that mirrors campus options.
    // The existing reassign list remains unchanged.
    // We include the empty option for optional filtering semantics.
    this.returnCampusList = [{ label: '', value: null }, ...(campuses ?? [])];
  }

  private syncIncludeTerminatedForUser(userID: number) {
    const normalizedUserID = Number(userID);
    if (!Number.isFinite(normalizedUserID) || normalizedUserID <= 0) {
      return;
    }

    const existsInActive = this.activeUsers.some(u => u.id === normalizedUserID);
    if (existsInActive) {
      return;
    }

    const existsInIncludingTerminated = this.usersIncludingTerminated.some(u => u.id === normalizedUserID);
    if (existsInIncludingTerminated) {
      this.includeTerminated = true;
      this.updateUsersList();
    }
  }

  onAssignedReturnSelectShow(equipment: AssignedEquipmentModel) {
    const k = this.assignedReturnRowKey(equipment);
    this.openAssignedReturnSelectCounts.set(k, (this.openAssignedReturnSelectCounts.get(k) ?? 0) + 1);
  }

  onAssignedReturnSelectHide(equipment: AssignedEquipmentModel) {
    const k = this.assignedReturnRowKey(equipment);
    const next = (this.openAssignedReturnSelectCounts.get(k) ?? 1) - 1;
    if (next <= 0) {
      this.openAssignedReturnSelectCounts.delete(k);
    } else {
      this.openAssignedReturnSelectCounts.set(k, next);
    }
  }

  isAssignedReturnSelectOpen(equipment: AssignedEquipmentModel): boolean {
    return (this.openAssignedReturnSelectCounts.get(this.assignedReturnRowKey(equipment)) ?? 0) > 0;
  }

  onBarcodeReturnSelectShow(equipment: AssignedEquipmentModel) {
    const k = this.barcodeReturnRowKey(equipment);
    this.openBarcodeReturnSelectCounts.set(k, (this.openBarcodeReturnSelectCounts.get(k) ?? 0) + 1);
  }

  onBarcodeReturnSelectHide(equipment: AssignedEquipmentModel) {
    const k = this.barcodeReturnRowKey(equipment);
    const next = (this.openBarcodeReturnSelectCounts.get(k) ?? 1) - 1;
    if (next <= 0) {
      this.openBarcodeReturnSelectCounts.delete(k);
    } else {
      this.openBarcodeReturnSelectCounts.set(k, next);
    }
  }

  isBarcodeReturnSelectOpen(equipment: AssignedEquipmentModel): boolean {
    return (this.openBarcodeReturnSelectCounts.get(this.barcodeReturnRowKey(equipment)) ?? 0) > 0;
  }

  private assignedReturnRowKey(e: AssignedEquipmentModel): string {
    const id = e.assetAssignedID ?? e.id;
    if (id != null) {
      return `a:${id}`;
    }
    return `e:${e.equipmentID ?? 0}-${e.barcode ?? ''}-${e.description ?? ''}`;
  }

  private barcodeReturnRowKey(e: AssignedEquipmentModel): string {
    const id = e.assetAssignedID ?? e.id;
    if (id != null) {
      return `b:${id}`;
    }
    return `b:${e.barcode ?? ''}-${e.description ?? ''}`;
  }

  private getReturnStatusLabel(assetReturnStatusID: number) {
    return this.returnStatusOptions.find(option => option.value === assetReturnStatusID)?.label ?? 'Working';
  }

  private getAllowedConditionIdsForStatus(statusId?: number): number[] {
    if (statusId === 1) {
      return [1, 2, 3, 4, 7];
    }
    if (statusId === 2) {
      return [4, 5, 6];
    }
    if (statusId === 3) {
      return [6];
    }
    return [];
  }

  private isConditionRequired(item: AssignedEquipmentModel): boolean {
    return item?.requireItemCondition === true
      && this.getAllowedConditionIdsForStatus(item.assetReturnStatusID).length > 0;
  }

  private canKeepUnsetCondition(item: AssignedEquipmentModel): boolean {
    const originalConditionId = item?.originalAssetItemConditionID ?? null;
    return !item?.assetItemConditionID && !originalConditionId;
  }

  private isConditionSelectionInvalid(item: AssignedEquipmentModel): boolean {
    if (!this.isConditionRequired(item)) {
      return false;
    }

    if (!item.assetItemConditionID) {
      return !this.canKeepUnsetCondition(item);
    }

    return !(item.allowedAssetItemConditions ?? []).some(x => x.id === item.assetItemConditionID);
  }

  private getReassignValidationError(items: AssignedEquipmentModel[]): string | null {
    if (!this.hasReassignTarget) {
      return null;
    }

    const hasProblematicStatus = (items ?? []).some(item =>
      item?.assetReturnStatusID === 3 || item?.assetReturnStatusID === 4);
    if (hasProblematicStatus) {
      return 'Cannot re-assign items with Destroyed or Not Returned status.';
    }

    return null;
  }

  private getDefaultReturnStatusForCondition(conditionId?: number): number {
    const id = Number(conditionId);
    if (!Number.isFinite(id) || id <= 0) {
      return 1; // Working
    }

    // Prefer Damaged for Poor/Unserviceable so current condition stays selectable by default.
    if (id === 5 || id === 6) {
      return 2; // Damaged
    }

    return 1; // Working
  }

  private updateAllowedAssetItemConditions(item: AssignedEquipmentModel) {
    if (!item) {
      return;
    }

    const allowedIds = this.getAllowedConditionIdsForStatus(item.assetReturnStatusID);
    item.allowedAssetItemConditions = this.assetItemConditionOptions.filter(x => allowedIds.includes(x.id));

    if (allowedIds.length === 0) {
      item.assetItemConditionID = null;
      return;
    }

    if (this.canKeepUnsetCondition(item)) {
      return;
    }

    if (!item.assetItemConditionID || !allowedIds.includes(item.assetItemConditionID)) {
      item.assetItemConditionID = item.allowedAssetItemConditions.length > 0 ? item.allowedAssetItemConditions[0].id : null;
    }
  }
}

